// The group sheet: "Add group" and Group options (rename, kind, target, delete).
import type { GroupDto, OverviewDto } from '../../../shared/api';
import { esc } from '../format';
import { groupShare, targetKind } from '../../../shared/groups';

/** Kinds in plain words. */
export const KIND_LABEL = { spending: 'Spending', set_aside: 'Money set aside (savings, investing)' } as const;

export function vGroupSheet(d: OverviewDto, g: GroupDto | null): string {
  const kind = g?.kind ?? 'spending';
  const opt = (v: 'spending' | 'set_aside') => `<option value="${v}" ${kind === v ? 'selected' : ''}>${KIND_LABEL[v]}</option>`;
  const pct = (id: string, v: number | null, label: string) =>
    `<label class="fld"><span>${label}</span><input id="${id}" class="n" inputmode="numeric" placeholder="none" value="${v ?? ''}"></label>`;
  const empty = g ? !d.categories.some((k) => k.groupId === g.id) : true;
  // A single target (one number, or the same in both) shows as one number: "About 10%".
  const single = g ? targetKind(g.targetMinPct, g.targetMaxPct) === 'about' : false;
  const min = g ? (single ? (g.targetMinPct ?? g.targetMaxPct) : g.targetMinPct) : null;
  const max = g && !single ? g.targetMaxPct : null;
  // The same line as the group's header on the Budget tab.
  const total = g ? d.categories.filter((k) => k.groupId === g.id).reduce((sum, k) => sum + k.budgetCents, 0) : 0;
  const share = g ? groupShare(total, d.totals.incomeCents, g.targetMinPct, g.targetMaxPct) : null;
  const current = share?.text ?? '';
  return `<div class="grab"></div><h2>${g ? 'Group options' : 'Add group'}</h2>
  <input type="hidden" id="g-id" value="${esc(g?.id ?? '')}">
  <label class="fld"><span>Name</span><input id="g-name" type="text" maxlength="60" placeholder="Fixed costs" value="${esc(g?.name ?? '')}"></label>
  <label class="fld"><span>Kind</span><select id="g-kind" class="sel">${opt('spending')}${opt('set_aside')}</select></label>
  <p class="hint">Its categories take this kind. Money set aside is not spending and is planned as transfers you mark done.</p>
  <div class="row2">${pct('g-min', min, 'Target, % of income')}${pct('g-max', max, 'Up to % (optional)')}</div>
  ${current ? `<p class="gshare ${share!.warning ? 'warn' : ''}" id="g-now">Now: ${esc(current)}</p>` : ''}
  <p class="hint">One number is a single percent, on target within 2 points. Two numbers are a range, like 50 to 60.</p>
  <p class="err" id="f-err"></p>
  <div class="btns"><button class="btn primary" data-a="group-save">${g ? 'Save changes' : 'Add group'}</button>${
    g
      ? empty
        ? `<button class="btn danger" data-a="group-delete" data-id="${esc(g.id)}">Delete group</button>`
        : '<p class="hint" id="g-del-why">To delete this group, move or delete its categories first.</p>'
      : ''
  }</div>`;
}
